/**
 * Sharetribe `keywords` only indexes listing title + description.
 * Listener names and bios live on the user, so we match those here
 * after a keyword-free listings.query.
 */

import { topicSearchHaystack } from './sessionTopics';
import { listeningStyleSearchHaystack } from './listeningStyles';
import { profileRevealSearchHaystack } from './profileReveals';

const tokensOf = value =>
  String(value || '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

export const listingSearchHaystack = (listing, author) => {
  const profile = author?.attributes?.profile || {};
  return [
    listing?.attributes?.title,
    listing?.attributes?.description,
    profile.displayName,
    profile.bio,
    profile.firstName,
    profile.lastName,
    topicSearchHaystack(
      listing?.attributes?.publicData?.topics,
      listing?.attributes?.publicData?.topicsOther,
      listing?.attributes?.publicData?.favoriteTeams
    ),
    topicSearchHaystack(
      profile.publicData?.interestedTopics,
      profile.publicData?.interestedTopicsOther
    ),
    listeningStyleSearchHaystack(listing?.attributes?.publicData?.listeningStyles),
    profileRevealSearchHaystack(listing),
  ]
    .filter(Boolean)
    .join(' ');
};

const tokenStem = token => (token.length >= 4 ? token.slice(0, 4) : token);

export const matchesListenerKeywords = (haystack, keywords) => {
  const query = String(keywords || '')
    .trim()
    .toLowerCase();
  if (!query) {
    return true;
  }
  const hay = String(haystack || '').toLowerCase();
  if (hay.includes(query)) {
    return true;
  }
  const queryTokens = tokensOf(query).filter(token => token.length >= 2);
  const hayTokens = tokensOf(hay);
  const hayStems = new Set(hayTokens.map(tokenStem));
  if (queryTokens.length === 0) {
    return false;
  }
  return queryTokens.every(qt => {
    if (hay.includes(qt)) {
      return true;
    }
    if (hayTokens.some(ht => ht === qt || (qt.length >= 3 && ht.includes(qt)))) {
      return true;
    }
    return hayStems.has(tokenStem(qt));
  });
};

export const authorFromIncluded = (listing, included = []) => {
  const authorId = listing?.relationships?.author?.data?.id?.uuid;
  if (!authorId) {
    return listing?.author || null;
  }
  return (
    included.find(
      item => item?.type === 'user' && (item.id?.uuid === authorId || item.id === authorId)
    ) ||
    listing?.author ||
    null
  );
};

/**
 * Write `keywords` onto a SearchPage query object. Empty query drops the param
 * so Any time / sort / conversation-type filters stay in place.
 *
 * @param {Object} [search]
 * @param {string} [keywords]
 * @returns {Object}
 */
export const applyKeywordSearchParam = (search = {}, keywords) => {
  const next = { ...search };
  delete next.page;
  const trimmed = String(keywords || '').trim();
  if (trimmed) {
    next.keywords = trimmed;
  } else {
    delete next.keywords;
  }
  return next;
};

/**
 * Filter a listings.query response by name / title / bio / topics.
 * Empty query leaves the current page as-is. A query that matches nobody
 * returns an empty list so results change with the input.
 */
export const applyListenerKeywordSearch = (response, keywords) => {
  const query = String(keywords || '').trim();
  const listings = response?.data?.data;
  if (!query || !Array.isArray(listings) || listings.length === 0) {
    return { response, matched: listings?.length || 0, fallback: false, keywords: query };
  }

  const included = response.data.included || [];
  const matched = listings.filter(listing =>
    matchesListenerKeywords(
      listingSearchHaystack(listing, authorFromIncluded(listing, included)),
      query
    )
  );

  const next = {
    ...response,
    data: {
      ...response.data,
      data: matched,
      meta: {
        ...(response.data.meta || {}),
        totalItems: matched.length,
        paginationLimit: 1,
        page: 1,
      },
    },
  };
  return { response: next, matched: matched.length, fallback: false, keywords: query };
};
